import {
	type CandidateProfileSection,
	candidateProfileSection,
} from '@jobilla/nexus-sdk/lib/types/CandidateProfileSection'
import { maxProfileEntriesPerSection } from '@jobilla/nexus-sdk/lib/types/MaxProfileEntriesPerSection'
import { maxProfileEntryLength } from '@jobilla/nexus-sdk/lib/types/MaxProfileEntryLength'
import { type FormEvent, useEffect, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { WorkHistoryHints } from './WorkHistoryHints'

export type ProfileEntrySaveError = 'limit' | 'failed'

const COUNTER_THRESHOLD = 0.8

export function ProfileEntryComposer(
	{ section, initialContents, isSaving, saveError, onSave, onCancel, onUnsavedChange }: {
		section: CandidateProfileSection
		initialContents: string
		isSaving: boolean
		saveError: ProfileEntrySaveError | null
		onSave: (contents: string) => void
		onCancel: (() => void) | null
		onUnsavedChange: (hasUnsavedChanges: boolean) => void
	},
) {
	const { t } = useTranslation('common')
	const id = useId()
	const [contents, setContents] = useState(initialContents)
	const [isEmpty, setIsEmpty] = useState(false)
	const isChanged = contents.trim() !== initialContents.trim()
	const length = contents.trim().length
	const isTooLong = length > maxProfileEntryLength
	const isNearLimit = length > maxProfileEntryLength * COUNTER_THRESHOLD

	useEffect(() => {
		onUnsavedChange(isChanged)
		return () => onUnsavedChange(false)
	}, [isChanged, onUnsavedChange])

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		const trimmedContents = contents.trim()

		if (trimmedContents === '') {
			setIsEmpty(true)
			return
		}

		onSave(trimmedContents)
	}

	return (
		<form onSubmit={handleSubmit} className="flex flex-col gap-1">
			<div className="flex flex-col overflow-hidden rounded-xl border border-grey-300 bg-white focus-within:border-brand-600 focus-within:ring-3 focus-within:ring-brand-500/25">
				<label htmlFor={id} className="sr-only">{t(`profile.${section}.input_label`)}</label>
				<textarea
					id={id}
					rows={4}
					maxLength={maxProfileEntryLength}
					value={contents}
					onChange={event => {
						setContents(event.target.value)
						setIsEmpty(false)
					}}
					placeholder={t(`profile.${section}.placeholder`)}
					className="resize-none border-none bg-transparent px-3.5 py-3 text-sm leading-relaxed text-grey-900 placeholder:text-grey-400 focus:outline-none"
				/>
				<div
					aria-hidden={!isNearLimit}
					className={`grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none ${
						isNearLimit ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
					}`}
				>
					<div className="overflow-hidden">
						<p
							className={`flex justify-end border-t border-grey-200 bg-grey-50 px-3.5 py-1.5 text-xs tabular-nums ${
								isTooLong ? 'text-red-600' : 'text-grey-500'
							}`}
						>
							{length} / {maxProfileEntryLength}
						</p>
					</div>
				</div>
				{section === candidateProfileSection.work_history ? <WorkHistoryHints contents={contents} /> : null}
			</div>
			{isEmpty || saveError !== null
				? (
					<p role="alert" className="mt-1 text-sm text-red-600">
						{isEmpty
							? t(`profile.${section}.error_empty`)
							: saveError === 'limit'
							? t(`profile.${section}.error_limit`, { max: maxProfileEntriesPerSection })
							: t('login.request_error.generic')}
					</p>
				)
				: null}
			<div className="-mr-2 flex justify-end gap-1">
				{onCancel === null
					? null
					: (
						<button
							type="button"
							onClick={onCancel}
							className="min-h-11 cursor-pointer px-2 text-sm font-medium text-grey-500 hover:text-grey-700"
						>
							{t(`profile.${section}.cancel`)}
						</button>
					)}
				<button
					type="submit"
					disabled={!isChanged || isTooLong || isSaving}
					className="min-h-11 cursor-pointer px-2 text-sm font-semibold text-brand-600 hover:text-brand-700 disabled:cursor-not-allowed disabled:text-grey-400"
				>
					{isSaving ? t(`profile.${section}.saving`) : t(`profile.${section}.save`)}
				</button>
			</div>
		</form>
	)
}
